import MetaRobots from 'components/Basic/Head/MetaRobots';
import SeoMeta from 'components/Basic/Head/SeoMeta';
import OrderSteps from 'components/Blocks/OrderSteps/OrderSteps';
import Webline from 'components/Layout/Webline/Webline';
import CartSummary from 'components/Pages/Cart/CartSummary';
import { CartSummaryLoadingWrapperStyled } from 'components/Pages/Cart/CartSummary/CartSummary.style';
import { EmptyCartWidget } from 'components/Pages/Cart/EmptyCartWidget/EmptyCartWidget';
import List from 'components/Pages/Cart/List';
import LoyaltyClub from 'components/Pages/Cart/LoyaltyClub';
import { useCurrentCart } from 'connectors/cart/Cart';
import { useSettings } from 'connectors/Settings/Settings';
import {
    LoyaltyClubPointsQueryDocumentApi,
    LoyaltyClubPointsQueryVariablesApi,
    useTransportsQueryApi,
} from 'graphql/generated';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { useActionEmptyCart } from 'hooks/cart/useActionEmptyCart';
import { useRemoveProductFromCart } from 'hooks/cart/useRemoveFromCart';
import { useGtmCartView } from 'hooks/gtm/useGtmCartView';
import { useGtmStaticPageView } from 'hooks/gtm/useGtmStaticPageView';
import { usePersoo } from 'hooks/persoo/usePersoo';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { PERSOO_IDS } from 'modules/persoo/persooTypes';
import { SpinnerIcon } from 'public/svg/IconsSvg';
import { useEffect } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { NextPageExtended } from 'types/global';
import { useGtmStaticPageViewEvent } from 'utils/Gtm/EventFactories';

const Cart: NextPageExtended<ServerSidePropsType> = () => {
    const settings = useSettings();
    const { isPersooAllowed } = usePersoo();
    const { loginLoading, cartUuid } = usePersistStore((s) => s);
    const t = useTypedTranslationFunction();
    const gtmStaticPageViewEvent = useGtmStaticPageViewEvent('cart');
    useGtmStaticPageView(gtmStaticPageViewEvent);
    useGtmCartView(gtmStaticPageViewEvent);
    const [_, refetchTransports] = useTransportsQueryApi({
        variables: { cartUuid },
        requestPolicy: 'cache-and-network',
        pause: true,
    });
    const { cart, isCartEmpty, isInitiallyLoaded } = useCurrentCart();

    const [removeItemFromCart, isRemovingItem] = useRemoveProductFromCart();
    const [actionEmptyCart, isActionEmptyCartFetching] = useActionEmptyCart();

    useEffect(() => {
        refetchTransports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.totalPrice.priceWithVat]);

    return (
        <>
            <MetaRobots content="noindex" />
            <SeoMeta title={t('Cart')} />
            <OrderSteps activeStep={1} tightTopMargin isOrderCreated={false} />
            {isInitiallyLoaded && loginLoading === 'not-loading' ? (
                <>
                    {isCartEmpty ? (
                        <Webline>
                            <EmptyCartWidget />
                        </Webline>
                    ) : (
                        <>
                            <List
                                idSuffix="-cart"
                                items={cart?.items}
                                removeItemFromCart={removeItemFromCart}
                                isRemovingItem={isRemovingItem || isActionEmptyCartFetching}
                            />
                            {settings?.loyaltyClub.enabled === true && <LoyaltyClub />}
                            <CartSummary
                                actionEmptyCart={actionEmptyCart}
                                isActionEmptyCartFetching={isActionEmptyCartFetching}
                            />
                            {isPersooAllowed && <div id={PERSOO_IDS.basketCarousel} />}
                        </>
                    )}
                </>
            ) : (
                <CartSummaryLoadingWrapperStyled>
                    <SpinnerIcon className="h-12 w-12" />
                </CartSummaryLoadingWrapperStyled>
            )}
        </>
    );
};

export const getServerSideProps = getServerSidePropsWrapper(({ domainConfig, redisClient, t }) => async (context) => {
    return initServerSideProps<LoyaltyClubPointsQueryVariablesApi>({
        context,
        domainConfig,
        redisClient,
        t,
        prefetchedQueries: [{ query: LoyaltyClubPointsQueryDocumentApi }],
    });
});

export default Cart;
